import { motion } from "framer-motion";
import projectData from "../data/projects";
import "../assets/css/Project.css";

// Animation Variants
const fadeIn = {
  hidden: { opacity: 0, scale: 0.95, y: 30 },
  show: (i) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.15,
      ease: "easeOut",
    },
  }),
};

const fadeInItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const Projects = () => {
  return (
    <section
      id="projects"
      className="min-h-screen bg-black text-white px-4 sm:px-6 md:px-12 lg:px-28 py-20 sm:py-24 font-sans"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="text-center mb-16 sm:mb-20"
      >
        <p className="text-sm text-pink-500 tracking-[0.15em] font-semibold uppercase mb-3 relative inline-block">
          Featured Case Studies
          <span className="block w-10 h-0.5 bg-pink-500 mx-auto mt-2 animate-pulse rounded-full"></span>
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
          Curated{" "}
          <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-transparent bg-clip-text animate-gradient-x drop-shadow-lg">
            work
          </span>
        </h2>
      </motion.div>

      {/* Project List */}
      <div className="space-y-24 sm:space-y-28">
        {projectData.map((project, i) => (
          <motion.div
            key={project.title}
            variants={fadeIn}
            initial="hidden"
            whileInView="show"
            custom={i}
            viewport={{ once: true, amount: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center"
          >
            {/* Left: Image Card */}
            <div className="relative project-card rounded-2xl border border-white/10 bg-gradient-to-br from-[#131313] via-[#1f1f1f] to-[#2c2c2c] p-4 sm:p-6 shadow-lg overflow-hidden">
              <div className="absolute inset-0 rounded-2xl bg-pink-500 blur-2xl opacity-10 -z-10" />
              <img
                src={project.image}
                alt={project.title}
                className="rounded-xl border border-white/10 w-full max-h-[300px] object-cover"
              />
              <p className="text-pink-300 font-medium mt-4 sm:mt-6 text-sm sm:text-base">
                {project.description ||
                  "A modern project built with clean UI, optimized performance and polished design."}
              </p>
            </div>

            {/* Right: Animated Project Details */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.1,
                  },
                },
              }}
              className="space-y-4 text-sm sm:text-base"
            >
              <motion.h3 variants={fadeInItem} className="text-xl sm:text-2xl font-semibold text-white">
                <span className="text-pink-500">{project.title}</span>
              </motion.h3>

              <motion.p variants={fadeInItem} className="text-gray-400">
                {project.detail ||
                  "This project was designed with a strong focus on responsive design, code optimization, and seamless user experience."}
              </motion.p>

              <motion.ul variants={fadeInItem} className="list-disc list-inside text-gray-300 space-y-1">
                <li>Fully responsive across devices</li>
                <li>Smooth framer-motion animations</li>
                <li>Integrated modern tools and APIs</li>
              </motion.ul>

              <motion.div variants={fadeInItem} className="flex flex-wrap gap-3 mt-4">
                {project.tech.map((t, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 text-xs sm:text-sm rounded-full bg-white/10 text-gray-100 border border-white/10"
                  >
                    {t}
                  </span>
                ))}
              </motion.div>

              <motion.a
                variants={fadeInItem}
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-pink-400 hover:underline text-sm"
              >
                View Project →
              </motion.a>
            </motion.div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Projects;
